import React, { useContext, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  AssessmentRounded as LedgerIcon,
  BalanceRounded as TrialBalanceIcon,
  AccountBalanceWalletRounded as CashBookIcon,
} from "@mui/icons-material";
import { AutoTaskAuthContext } from "../../../Context/AutoTaskContext";
import PawnNavButtons from "../../PawnShop/pawnNavButtons";
import {
  reportFixedSx,
  reportSwitcherStripSx,
} from "../../PawnShop/reportLayout";
import PawnIndividualLedger from "./PawnIndividualLedger";
import PawnTrialBalance from "./PawnTrialBalance";
import PawnCashBankBook from "./PawnCashBankBook";
import { BG_ALT, BLUE, BORDER, INK, INK_MUTED } from "./ledgerReportKit";

/**
 * The pawn module's Ledger Reports screen — the same shape as /pawn/reports:
 * one page, a chip switcher at the top right, and one report body that flexes
 * into the leftover height.
 *
 * It replaces the tabbed card the Travel ERP screen used inside pawn. The
 * Travel and Daily Tick hosts still render that original screen — see
 * ../Ledger.js, which picks between the two.
 */
const REPORTS = {
  ledger: {
    label: "Ledger",
    title: "Individual Ledger",
    icon: <LedgerIcon />,
    Component: PawnIndividualLedger,
  },
  trial: {
    label: "Trial Balance",
    title: "Trial Balance",
    icon: <TrialBalanceIcon />,
    Component: PawnTrialBalance,
  },
  cashbook: {
    label: "Cash Book",
    title: "Cash / Bank Book",
    icon: <CashBookIcon />,
    Component: PawnCashBankBook,
  },
};

const DEFAULT_TYPE = "ledger";

const PawnLedgerReports = () => {
  const { currentStoreInfo, defaultFY } = useContext(AutoTaskAuthContext) || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // The active report comes from the URL (?type=), so a link into one report
  // stays shareable and the browser Back button works between them.
  //
  // Coming BACK from the accounts-entry screen there is no query string —
  // AccountEntry navigates to a bare /accounts-ledger — so the chip the
  // drill-down started from rides home in router state instead.
  const rawType = searchParams.get("type") || location.state?.ledgerType;
  const type = REPORTS[rawType] ? rawType : DEFAULT_TYPE;
  const active = REPORTS[type];
  const ActiveReport = active.Component;

  const handleChange = (_e, next) => {
    if (!next || next === type) return;
    // replace: switching reports shouldn't stack browser-history entries.
    setSearchParams({ type: next }, { replace: true });
  };

  const chips = useMemo(
    () => (
      <ToggleButtonGroup
        value={type}
        exclusive
        size="small"
        onChange={handleChange}
        sx={{
          // Never `100%`: the chips keep their natural width and the strip
          // around them scrolls, so they can't widen the page on a phone.
          width: "auto",
          "& .MuiToggleButton-root": {
            textTransform: "none",
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 0.6, md: 0.75 },
            fontSize: { xs: "0.72rem", sm: "0.8rem" },
            fontWeight: 700,
            color: INK_MUTED,
            borderColor: BORDER,
            gap: 0.5,
          },
          "& .Mui-selected": {
            bgcolor: `${BLUE} !important`,
            color: "#fff !important",
          },
        }}
      >
        {Object.entries(REPORTS).map(([key, r]) => (
          <ToggleButton key={key} value={key}>
            {React.cloneElement(r.icon, {
              sx: { fontSize: { xs: 16, md: 18 }, mr: 0.5 },
            })}
            {r.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type],
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: BG_ALT,
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
      }}
    >
      {/* ── Top bar: heading + report switcher ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "stretch", lg: "center" },
          justifyContent: "space-between",
          gap: { xs: 1, sm: 1.5 },
          mb: { xs: 1.5, md: 2 },
          minWidth: 0,
          ...reportFixedSx,
        }}
      >
        {/* Heading — below the chips on mobile, left of them on desktop. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            order: { xs: 2, lg: 1 },
            minWidth: 0,
          }}
        >
          <PawnNavButtons />
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            {active.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK,
              }}
            >
              {active.title}
            </Typography>
            <Typography noWrap sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
              {[
                currentStoreInfo?.name,
                defaultFY ? `FY ${defaultFY}-${defaultFY + 1}` : null,
              ]
                .filter(Boolean)
                .join("  ·  ")}
            </Typography>
          </Box>
        </Box>

        {/* Switcher — top on mobile, right edge on desktop. */}
        <Box sx={{ order: { xs: 1, lg: 2 }, ...reportSwitcherStripSx }}>
          {chips}
        </Box>
      </Box>

      {/* Only the selected report is mounted, so only it fetches. */}
      <ActiveReport embedded />
    </Box>
  );
};

export default PawnLedgerReports;
